import React, { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import { formatDate, parseDate, dateValid } from 'Utils';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import Icon from 'Components/Icon';
import 'react-day-picker/lib/style.css';
import styles from './styles.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

export const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export const WEEKDAYS_LONG = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

export const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const holidays = [
  new Date(2022, 9, 3),
  new Date(2022, 11, 25),
  new Date(2022, 11, 26),
  new Date(2023, 0, 1),
  new Date(2023, 3, 7),
  new Date(2023, 3, 10),
  new Date(2023, 4, 1),
  new Date(2023, 4, 18),
  new Date(2023, 4, 29),
  new Date(2023, 0, 0),
  new Date(2023, 9, 3),
  new Date(2023, 11, 25),
  new Date(2023, 11, 26),
];

export default class Datepicker extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired,
    minDate: PropTypes.instanceOf(Date),
    disabled: PropTypes.bool,
    dayPickerOpenUpwards: PropTypes.bool,
    // is the date optional? important to know due handling of empty inputs
    optional: PropTypes.bool,
    disabledDaysOfWeek: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  };

  static defaultProps = {
    value: new Date(),
    disabled: false,
    dayPickerOpenUpwards: false,
    optional: false,
  };

  constructor(props) {
    super(props);

    let value = props.value;

    if (!props.optional) {
      if (props.minDate && props.minDate >= value) {
        value = props.minDate;
      }
    }

    this.state = {
      value,
      // date is not immutable
      initialValue: value ? new Date(value) : undefined,
      // a new field to observe the real input value
      input: value ? new Date(value) : '',
    };

    this.datepickerRef = createRef();
  }

  onBlur = ({ target }) => {
    const { input, initialValue } = this.state;
    const inputDate = parseDate(target.value);
    const { optional } = this.props;

    if (input !== '' || (!optional && input === '')) {
      this.setState({
        value: initialValue,
        // null = 1.1.1970 => valid date
        initialValue:
          inputDate !== null && dateValid(inputDate) ? new Date(this.props.value) : initialValue,
        input: initialValue ? new Date(initialValue) : '',
      });
    }
  };

  onChange = (value, modifiers, inputField) => {
    if (modifiers.disabled) {
      return;
    }

    this.setState({
      value,
      initialValue: value ? new Date(value) : this.state.initialValue,
      input: value ? new Date(value) : inputField.getInput().value,
    });
    this.props.onChange(value, this.props.name);
  };

  show = () =>
    this.datepickerRef &&
    this.datepickerRef.current &&
    this.datepickerRef.current.getInput().focus();

  resetValue = (value) =>
    this.setState({
      value,
      initialValue: value ? new Date(value) : undefined,
      input: value ? new Date(value) : '',
    });

  render() {
    const { minDate, disabled, dayPickerOpenUpwards, disabledDaysOfWeek } = this.props;
    const { value, input } = this.state;

    const modifiers = {};

    if (minDate) {
      modifiers.fromMonth = minDate;
      modifiers.disabledDays = [
        ...holidays,
        {
          before: minDate,
        },
        {
          daysOfWeek: disabledDaysOfWeek || [],
        },
      ];
    }
    const className = cx(styles.datePickerRoot, {
      [styles.disabled]: disabled,
    });

    return (
      <div className={className} onClick={this.show}>
        <DayPickerInput
          inputProps={{
            onBlur: this.onBlur,
            maxLength: 10,
          }}
          classNames={{
            container: 'DayPickerInput',
            overlay: dayPickerOpenUpwards
              ? 'DayPickerInput-Overlay DayPickerInput-Overlay--open-upwards'
              : 'DayPickerInput-Overlay',
            overlayWrapper: 'DayPickerInput-OverlayWrapper',
          }}
          ref={this.datepickerRef}
          value={input}
          onDayChange={this.onChange}
          formatDate={(date) => formatDate(date, 'P')}
          parseDate={parseDate}
          placeholder="DD.MM.JJJJ"
          dayPickerProps={{
            locale: 'de',
            months: MONTHS,
            weekdaysLong: WEEKDAYS_LONG,
            weekdaysShort: WEEKDAYS_SHORT,
            showOutsideDays: true,
            fixedWeeks: true,
            selectedDays: value,
            month: value,
            ...modifiers,
          }}
        />
        <Icon name="calendar" className={styles.icon} inline={false} />
      </div>
    );
  }
}
